import { useId, type ComponentPropsWithoutRef, type ReactNode } from "react";

import { cn } from "../../lib/cn";

export type TextFieldProps = Omit<ComponentPropsWithoutRef<"input">, "size"> & {
  label: string;
  helperText?: string;
  error?: string;
  leftIcon?: ReactNode;
};

export function TextField({
  label,
  helperText,
  error,
  leftIcon,
  id,
  className,
  ...rest
}: TextFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedById = error
    ? `${inputId}-error`
    : helperText
      ? `${inputId}-help`
      : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-label text-text">
        {label}
      </label>
      <div className="relative">
        {leftIcon && (
          <span
            className="text-text-tertiary pointer-events-none absolute inset-y-0 left-3 flex items-center"
            aria-hidden="true"
          >
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedById}
          className={cn(
            "block h-10 w-full rounded-md border border-separator bg-surface text-body text-text placeholder:text-placeholder",
            "focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent",
            leftIcon ? "py-2 pl-10 pr-3" : "px-3 py-2",
            error && "ring-1 ring-inset ring-error",
            className,
          )}
          {...rest}
        />
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="text-caption text-error-text">
          {error}
        </p>
      ) : helperText ? (
        <p id={`${inputId}-help`} className="text-caption text-text-secondary">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
